import styled, { css } from 'styled-components';

import type { RefinedPremiumContacts } from '@xing-com/crate-upsell-types';
import { spaceS } from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

import type { RenderLocation } from '../typings';

import { facepileMesssage, renderFacepile } from './contacts-facepile';

export type ProfileContactsWithProductProps = {
  renderLocation: RenderLocation;
  premiumContacts: RefinedPremiumContacts;
  isLargerSize?: boolean;
  isInRow?: boolean;
  className?: string;
};

export function ProfileContactsWithProduct({
  renderLocation,
  isLargerSize = false,
  premiumContacts,
  isInRow = false,
  className = '',
}: ProfileContactsWithProductProps): JSX.Element {
  if (
    !premiumContacts ||
    premiumContacts.total <= 0 ||
    premiumContacts.contacts.length <= 0
  )
    return <></>;

  const { contacts, total } = premiumContacts;

  // shows at least 2 or up to 4 avatars
  const maxAvatars = isInRow ? 2 : total < 5 ? total : 4;

  if (renderLocation === 'offerbox') {
    return (
      <OfferBoxFacepileMessage
        data-testid="offerbox"
        className={className}
        isInRow
      >
        {renderFacepile(contacts, 2)}
        <ContactsDisplayParagraph size="xsmall" sizeWide="small">
          {facepileMesssage(contacts[0].profileName ?? '')}
        </ContactsDisplayParagraph>
      </OfferBoxFacepileMessage>
    );
  }

  if (renderLocation === 'salutation') {
    return (
      <SalutationFacepileMessage
        data-testid="salutation"
        className={className}
        isInRow={isInRow}
      >
        {renderFacepile(
          contacts,
          maxAvatars,
          isInRow || total < 5 ? undefined : total - maxAvatars
        )}
        {isLargerSize ? (
          <ContactsDisplayHeadline size="xxlarge" sizeWide="xxxlarge">
            {facepileMesssage(contacts[0].profileName ?? '')}
          </ContactsDisplayHeadline>
        ) : (
          <ContactsDisplayParagraph sizeWide="small" fontWeight="bold">
            {facepileMesssage(contacts[0].profileName ?? '')}
          </ContactsDisplayParagraph>
        )}
      </SalutationFacepileMessage>
    );
  }

  return <></>;
}

const SalutationFacepileMessage = styled.div<{
  isInRow: boolean;
}>`
  display: grid;
  grid-gap: ${spaceS};

  ${({ isInRow }) =>
    isInRow &&
    css`
      grid-template-columns: auto minmax(0, 1fr);
      align-items: start;
    `}

  button {
    height: auto;
  }
`;

const OfferBoxFacepileMessage = styled.div<{
  isInRow: boolean;
}>`
  display: grid;
  grid-gap: ${spaceS};

  ${({ isInRow }) =>
    isInRow &&
    css`
      grid-template-columns: auto minmax(0, 1fr);
      align-items: center;
    `}

  button {
    height: auto;
  }
`;

const ContactsDisplayParagraph = styled(BodyCopy)`
  margin: 0;
`;

const ContactsDisplayHeadline = styled(Headline)`
  margin: 0;
`;
